import { getMode, isSewer, modeId } from '../lib/profiles';
import { askConfirm } from '../lib/askConfirm';
import { useEffect, useRef, useState } from 'react';
import { askDepth } from '../components/DepthPrompt';
import { snapCad } from '../lib/dxf';
import { MED_COLOR, itemLabel, noDn, catItem } from '../lib/catalog';

import { toast } from 'sonner';
import { uid, samePt, resolvePt, pipeLatLngs, haversine } from '../lib/model';
import { gatePoint, precAsk } from '../lib/precision';
import { getNc, setNc, openEnds, shutArea, isValve } from '../lib/netCheck';
import { flyTo, getVis, setVis, pipeCat } from '../lib/vis';

const splitPipes = (pipes, hit, fid) => pipes.flatMap((pp) => {
  if (pp.id !== hit.pipeId) return [pp];
  return [{ ...pp, points: [...pp.points.slice(0, hit.k + 1), { fid }] }, { ...pp, insp: undefined, id: uid('pipe'), name: `${pp.name} (2)`, points: [{ fid }, ...pp.points.slice(hit.k + 1)] }];
});
const catFields = (d) => (d.catId ? { catId: d.catId, color: d.color, sym: d.sym, cat: d.snap, ...(d.sub ? { sub: d.sub } : {}), ...(d.prod ? { prod: d.prod } : {}), ...(d.med ? { med: d.med } : {}) } : {});

export default function useNetwork({ t, project, updateProject, mapRef, repId }) {
  const [draft, setDraftRaw] = useState(null);
  const [lastFit, setLastFit] = useState(null);
  const draftRef = useRef(null);
  draftRef.current = draft;
  const fittings = project?.fittings || [];

  // main pipe bearing + DN at the docking point (cross/tee rotation, reduction)
  const hostInfo = (hit) => {
    const pp = hit && project.pipes.find((x) => x.id === hit.pipeId);
    const P = pp ? pipeLatLngs(pp, project.fittings || []) : [];
    const a = P[hit?.k], b = P[(hit?.k ?? 0) + 1];
    if (!a || !b) return {};
    return { hostPipe: pp.id, hostDn: pp.dn || '', angle: Math.round((Math.atan2((b[1] - a[1]) * Math.cos((a[0] * Math.PI) / 180), b[0] - a[0]) * 180) / Math.PI + 360) % 360 };
  };
  // nearest hit on another pipe (excluding ids)
  const hitOther = (pt, skip = []) => project.pipes.filter((pp) => !skip.includes(pp.id)).map((pp) => mapRef.current.nearestPipe(pt, pp.id)).filter(Boolean).sort((a, b) => a.d - b.d)[0] || null;
  // dock a free point onto an existing pipe: reuse vertex/component or insert a tee (splits the pipe)
  const dockAt = (pt, skip) => {
    const hit = hitOther(pt, skip);
    if (!hit) return null;
    const pp = project.pipes.find((x) => x.id === hit.pipeId), fits = project.fittings || [];
    const v = [pp.points[hit.k], pp.points[hit.k + 1]].find((x) => { const q = resolvePt(x, fits); return q && haversine(q, hit.pt) <= 1; });
    if (v) return Array.isArray(v) ? [...v] : { fid: v.fid };
    // sewers have no valves: a junction on a sewer/rain pipe is a branch fitting (Abzweig / Sattelstück, DIN EN 1401 / DWA-A 139)
    const sew = pp.type === 'sewer' || pp.med === 'sewer' || pp.med === 'rain', az = sew && catItem('k09');
    const f = az ? { id: uid('fit'), type: 'other', state: 'none', name: `${az.name} (${fits.length + 1})`, note: '', lat: hit.pt[0], lon: hit.pt[1], media: [], memos: [], reportId: repId, catId: az.id, color: az.color, sym: az.sym, cat: { name: az.name, dim: az.dim, unit: az.unit }, ...hostInfo(hit) }
      : { id: uid('fit'), type: 'tee3', state: 'none', name: `${t.fit_tee3} (${fits.length + 1})`, note: '', lat: hit.pt[0], lon: hit.pt[1], media: [], memos: [], reportId: repId, ...hostInfo(hit) };
    updateProject((p) => ({ ...p, fittings: [...(p.fittings || []), f], pipes: splitPipes(p.pipes, hit, f.id) }));
    toast.success(az ? t.azwAuto || t.teeAuto : t.teeAuto, { id: 'tee-auto' });
    return { fid: f.id };
  };
  // nearest component, excluding the ones already on this line (no loop back to the start manhole) and beyond 25 m
  const nearFit = (pt, skip = [], max = 25) => (project.fittings || []).filter((f) => !skip.includes(f.id)).map((f) => ({ f, d: haversine(pt, [f.lat, f.lon]) })).filter((x) => x.d <= max).sort((a, b) => a.d - b.d)[0] || null;
  const fname = (f) => f.name || t['fit_' + f.type];
  // attach component fid to a point list end: replace the free end if close, else extend
  const withEnd = (points, fid, first) => {
    const fits = project.fittings || [], f = fits.find((x) => x.id === fid), i = first ? 0 : points.length - 1;
    const q = resolvePt(points[i], fits), rep = q && f && Array.isArray(points[i]) && haversine(q, [f.lat, f.lon]) <= 3;
    if (first) return rep ? [{ fid }, ...points.slice(1)] : [{ fid }, ...points];
    return rep ? [...points.slice(0, -1), { fid }] : [...points, { fid }];
  };
  const addPoint = (pt, dd, ex) => {
    const extra = draftRef.current?.priv && draftRef.current.kind === 'fitting' ? { free: true, privUnknown: true, ...ex } : ex;
    const d = dd || draftRef.current;
    if (!d) return;
    if (d.kind === 'fitting') {
      if (!Array.isArray(pt)) return;
      if (!extra?.gnss && !extra?.prec) {
        gatePoint(t, pt).then((g) => {
          if (!g) return;
          if (g.warn) toast.warning(t.prNoRtk, { id: 'prec-warn', duration: 6000 });
          addPoint(g.pt, d, { ...extra, prec: 1, ...(g.gnss ? { gnss: g.gnss } : {}), ...(g.draft ? { draft: true } : {}) });
        });
        return;
      }
      const hit = extra?.gnss || extra?.free ? null : mapRef.current.nearestPipe(pt);
      let at = hit ? hit.pt : pt;
      // garden: keep new parts off the project / section flag so they can be tapped right away
      const pins = [project, ...(project.reports || [])].filter((x) => x?.lat != null).map((x) => [x.lat, x.lon]);
      if (!hit && !extra?.gnss && getMode() === 'garden' && pins.some((q) => haversine(q, at) < 2.5)) { at = [at[0], at[1] + 3 / (111320 * Math.cos((at[0] * Math.PI) / 180))]; toast.message(t.spMoved, { id: 'sp-moved' }); }
      const f = { id: uid('fit'), type: d.type, state: d.type === 'leak' ? 'defect' : 'none', name: `${d.label || t['fit_' + d.type]} (${fittings.length + 1})`, note: '', lat: at[0], lon: at[1], media: [], memos: [], reportId: repId, ...(d.type === 'leak' ? { severity: 'medium' } : {}), ...catFields(d), ...hostInfo(hit), ...extra };
      delete f.free; delete f.prec;
      // name + survey point number from the current list (several quick taps must not repeat "(1)")
      updateProject((p) => {
        const list = p.fittings || [], ff = { ...f, name: `${d.label || t['fit_' + d.type]} (${list.length + 1})`, ...(getMode() === 'survey' ? { pno: Math.max(0, ...list.map((x) => +x.pno || 0)) + 1 } : {}), ...(p.planOn && ['detect_inhouse', 'detect_roof'].includes(modeId()) ? { floor: p.planOn } : {}) };
        return { ...p, fittings: [...list, ff], pipes: hit ? splitPipes(p.pipes, hit, f.id) : p.pipes };
      });
      if (hit) toast.success(t.splitDone);
      setLastFit({ id: f.id, at: Date.now() });
      if (['leak', 'kks', 'cfault', 'locate', 'tracer', 'gmic'].includes(d.type)) { setDraft(null); window.dispatchEvent(new CustomEvent('giss-select', { detail: { kind: 'fitting', id: f.id } })); }
      return;
    }
    // pits ignore network snapping: a tapped fitting / pipe point becomes a plain corner
    if (d.kind === 'pit' && pt && !Array.isArray(pt)) pt = resolvePt(pt, project.fittings || []);
    if (d.kind === 'pit' && (!Array.isArray(pt) || d.dragging || (d.shape !== 'polygon' && d.points.length >= 2))) return;
    if (d.kind === 'pipe' && !d.points.length && Array.isArray(pt)) pt = dockAt(pt) || pt;
    if (samePt(d.points[d.points.length - 1], pt)) return;
    setDraft({ ...d, points: [...d.points, pt], ...(extra?.prof ? { profile: [...(d.profile || []), extra.prof] } : {}) });
  };

  const addAtCrosshair = () => {
    const c = mapRef.current.getCenter();
    const sn = snapCad(project.cad, [c.lat, c.lng]);
    if (sn) toast.message(t.cadSnapped);
    addPoint(sn || [c.lat, c.lng]);
  };

  // new pipe: ask how the start connects (last placed object / crosshair / tap a component)
  const askedRef = useRef(false);
  useEffect(() => {
    const d = draft;
    if (!d || d.kind !== 'pipe') { askedRef.current = false; return; }
    if (askedRef.current || d.points.length) return;
    askedRef.current = true;
    const fits = project?.fittings || [];
    if (!fits.length) return;
    const last = fits.find((x) => x.id === lastFit?.id) || fits[fits.length - 1];
    const c0 = mapRef.current?.getCenter(), far = c0 ? haversine([c0.lat, c0.lng], [last.lat, last.lon]) : 0;
    precAsk('pipeStart', { name: last.name || t['fit_' + last.type], d: far }).then((c) => {
      const cur = draftRef.current;
      if (cur?.kind !== 'pipe' || cur.points.length) return;
      if (c === 'last') { addPoint({ fid: last.id }); toast.success(`${t.psLinked}: ${last.name || t['fit_' + last.type]}`); return; }
      if (c === 'cross') {
        const cc = mapRef.current.getCenter(), near = fits.map((f) => ({ f, d: haversine([cc.lat, cc.lng], [f.lat, f.lon]) })).sort((a, b) => a.d - b.d)[0];
        if (near && near.d <= 2) { addPoint({ fid: near.f.id }); toast.success(`${t.psLinked}: ${near.f.name || ''}`); } else addPoint([cc.lat, cc.lng]);
        return;
      }
      if (c === 'pick') toast.message(t.psPickHint, { duration: 7000, id: 'ps-pick' });
    });
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickRef = useRef(null);
  const onFittingClick = (fid) => {
    const d = draftRef.current;
    if (d?.kind === 'pit') { addPoint({ fid }); return; }
    if (d?.kind !== 'pipe') return;
    if (d.pickEnd) { if (d.points.some((q) => q?.fid === fid)) { toast.error(t.peLoop); return; } toast.dismiss('pe-pick'); commitPipe(withEnd(d.points, fid)); return; }
    if (d.points[0]?.fid === fid) { toast.error(t.peLoop); return; }
    toast.dismiss('ps-pick');
    addPoint({ fid });
  };
  // intercepts a component tap for open-end fixing / shut-off analysis; true = consumed
  const pickFit = (fid) => {
    if (pickRef.current) { const f = pickRef.current; pickRef.current = null; f(fid); return true; }
    if (!getNc().arm) return false;
    const f = (project.fittings || []).find((x) => x.id === fid);
    if (!isValve(f)) { toast.error(t.shNotValve, { id: 'sh-nv' }); return true; }
    const r = shutArea(project.pipes, project.fittings, fid);
    if (r.empty) { toast.error(t.shNoNet); return true; }
    setNc({ arm: false, shut: r });
    toast.dismiss('sh-arm');
    flyTo(r.edges.flat());
    return true;
  };

  const savePipe = (d, points) => {
    const pipe = { id: uid('pipe'), name: d.label || t.pipeDefault + (project.pipes.length + 1), type: d.type, dn: d.dn && !Number.isNaN(+d.dn) ? +d.dn : d.dn || '', points, reportId: repId, ...(d.profile?.length ? { profile: d.profile } : {}), ...(d.private ? { private: true } : {}), ...(d.type === 'heat' ? { sub: 'vl' } : {}), ...catFields(d) };
    updateProject((p) => ({ ...p, pipes: [...p.pipes, p.planOn && ['detect_inhouse', 'detect_roof'].includes(modeId()) ? { ...pipe, floor: p.planOn } : pipe] }));
    if (!getVis()[pipeCat(pipe)]) setVis({ [pipeCat(pipe)]: true });
    if (!(isSewer() && pipe.points[0]?.fid && pipe.points[pipe.points.length - 1]?.fid)) askDepth(pipe.id);
  };
  const commitPipe = (points) => {
    const d = draftRef.current;
    if (!d) return;
    savePipe(d, points);
    setDraftRaw(null);
  };
  // leaving an unfinished pipe (other tool, project switch, done) keeps it instead of discarding it
  const setDraft = (v) => {
    const d = draftRef.current, next = typeof v === 'function' ? v(d) : v;
    if (d?.kind === 'pipe' && !d.pickEnd && d.points.length >= 2 && (!next || next.kind !== 'pipe' || !next.points?.length)) { savePipe(d, d.points); toast.success(t.pipeKept); }
    draftRef.current = next;
    setDraftRaw(next);
  };
  const cancelDraft = async () => { const d = draftRef.current; if (d?.kind === 'pipe' && d.points.length >= 2 && !(await askConfirm(t.pipeDiscard, { cancel: t.btnCancel }))) return; draftRef.current = null; setDraftRaw(null); };
  // finish: end on a pipe -> auto tee; free end -> ask how it connects
  const finishPipe = async (pts) => {
    const d = draftRef.current;
    if (!d) return;
    const points = Array.isArray(pts) ? pts : d.points;
    if (points.length < 2) return toast.error(t.pipeNeed2);
    const last = points[points.length - 1];
    if (!Array.isArray(last)) return commitPipe(points);
    const dock = dockAt(last);
    if (dock) return commitPipe([...points.slice(0, -1), dock]);
    const nf = nearFit(last, points.filter((q) => q?.fid).map((q) => q.fid));
    if (!nf) return commitPipe(points);
    const c = await precAsk('pipeEnd', { name: `${fname(nf.f)}, ${nf.d.toFixed(1).replace('.', ',')} m` });
    if (draftRef.current !== d) return;
    if (c === 'last') { toast.success(`${t.psLinked}: ${fname(nf.f)}`); return commitPipe(withEnd(points, nf.f.id)); }
    if (c === 'cross') {
      const cc = mapRef.current.getCenter(), ll = [cc.lat, cc.lng], n2 = nearFit(ll);
      if (n2 && n2.d <= 2) return commitPipe(withEnd(points, n2.f.id));
      return commitPipe([...points, dockAt(ll) || ll]);
    }
    if (c === 'pick') { setDraft({ ...d, points, pickEnd: true }); toast.message(t.pePickHint, { duration: 8000, id: 'pe-pick' }); return; }
    if (c === 'free') commitPipe(points);
  };

  const setEnd = (pipeId, i, fn) => updateProject((p) => ({ ...p, pipes: p.pipes.map((pp) => (pp.id === pipeId ? { ...pp, points: fn(pp.points) } : pp)) }));
  // fix an open pipe end (red marker tap)
  const fixEnd = async ({ pipeId, i }) => {
    const pp = project.pipes.find((x) => x.id === pipeId);
    const pt = pp && resolvePt(pp.points[i], project.fittings || []);
    if (!pt) return;
    const first = i === 0, nf = nearFit(pt, pp.points.filter((q) => q?.fid).map((q) => q.fid)), hit = hitOther(pt, [pipeId]);
    const c = await precAsk('endFix', { pipe: pp.name, near: nf ? fname(nf.f) : '', d: nf ? nf.d.toFixed(1).replace('.', ',') : '', tee: !!hit });
    const link = (fid) => { if (pp.points.some((q) => q?.fid === fid)) { toast.error(t.peLoop); return; } setEnd(pipeId, i, (P) => withEnd(P, fid, first)); toast.success(t.oeFixed); };
    if (c === 'near') return link(nf.f.id);
    if (c === 'tee') { const dk = dockAt(pt, [pipeId]); if (dk) { setEnd(pipeId, i, (P) => P.map((x, j) => (j === i ? dk : x))); toast.success(t.oeFixed); } return; }
    if (c === 'pick') { pickRef.current = link; toast.message(t.oePickHint, { duration: 8000, id: 'oe-pick' }); return; }
    if (c === 'cap') {
      const f = { id: uid('fit'), type: 'other', sym: 'EK', state: 'none', name: `${t.oeCapName} ${(project.fittings || []).length + 1}`, note: '', lat: pt[0], lon: pt[1], media: [], memos: [], reportId: repId };
      updateProject((p) => ({ ...p, fittings: [...(p.fittings || []), f], pipes: p.pipes.map((x) => (x.id === pipeId ? { ...x, points: x.points.map((q, j) => (j === i ? { fid: f.id } : q)) } : x)) }));
      toast.success(t.oeFixed);
    }
  };
  const fixRef = useRef(fixEnd);
  fixRef.current = fixEnd;
  useEffect(() => {
    const on = (e) => fixRef.current(e.detail);
    window.addEventListener('giss-open-end', on);
    return () => window.removeEventListener('giss-open-end', on);
  }, []);
  const toggleEnds = () => {
    const on = !getNc().ends;
    setNc({ ends: on });
    if (!on) return toast.message(t.oeOff);
    const n = openEnds(project?.pipes, project?.fittings).length;
    if (n) toast.error(t.oeCount.replace('{n}', n), { duration: 6000 }); else toast.success(t.oeNone);
  };
  const armShut = () => { setDraft(null); setNc({ arm: true, shut: null }); toast.message(t.shArm, { duration: 10000, id: 'sh-arm' }); };

  const patchList = (key, id, patch) => updateProject((p) => ({
    ...p,
    [key]: p[key].map((x) => (x.id === id ? { ...x, ...(typeof patch === 'function' ? patch(x) : patch) } : x)),
  }));

  const deleteFitting = async (id) => {
    if (!(await askConfirm(t.confirmDelFit, { ok: t.pitDelete, cancel: t.btnCancel }))) return false;
    updateProject((p) => ({
      ...p,
      pipes: p.pipes.map((pp) => ({ ...pp, points: pp.points.map((pt) => (pt.fid === id ? resolvePt(pt, p.fittings) : pt)) })),
      fittings: p.fittings.filter((x) => x.id !== id),
    }));
    return true;
  };

  const netUnlocked = project?.networkLocked === false;
  const itemDraft = (it, dn, keep = {}) => ({ ...keep, kind: it.kind === 'pipe' ? 'pipe' : 'fitting', type: it.type || (it.kind === 'pipe' ? 'water' : 'other'), catId: it.id, color: it.kind === 'pipe' && MED_COLOR[it.med] ? MED_COLOR[it.med] : it.color, sym: it.sym, sub: it.sub, dn: it.kind === 'pipe' && !noDn(it) ? dn || keep.dn || '' : '', label: it.prod ? `${it.prod.name} ${it.prod.dim}`.trim() : itemLabel(it, it.kind === 'pipe' ? dn || keep.dn : ''), prod: it.prod || null, med: it.med || null,
    snap: { name: it.name, dim: it.dim, unit: it.unit, price: it.price, install: it.install }, points: keep.points || [] });
  const startItem = (it, dn, keep) => setDraft(itemDraft(it, dn, keep));
  // place a catalogue point item / leak directly at a coordinate (Street View take-over)
  const placeAt = (it, pt) => addPoint(pt, it ? itemDraft(it) : { kind: 'fitting', type: 'leak' });

  return {
    placeAt,
    draft,
    fittings,
    setDraft,
    cancelDraft,
    draftRef,
    addPoint,
    addAtCrosshair,
    finishPipe,
    onFittingClick,
    pickFit,
    toggleEnds,
    armShut,
    netUnlocked,
    setNetLocked: (locked) => {
      updateProject((p) => ({ ...p, networkLocked: locked }));
      if (locked) toast.success(t.netConfirmed);
    },
    moveFitting: (id, lat, lon) => patchList('fittings', id, { lat, lon }),
    insertVertex: (pid, ll) => {
      const hit = mapRef.current.nearestPipe([ll.lat, ll.lng], pid);
      if (!hit) return;
      patchList('pipes', pid, (pp) => ({ points: [...pp.points.slice(0, hit.k + 1), hit.pt, ...pp.points.slice(hit.k + 1)] }));
      toast.success(t.vertexAdded);
    },
    deleteVertex: (pid, i) => {
      const pp = project.pipes.find((x) => x.id === pid);
      if (!pp || pp.points.length <= 2) return toast.error(t.pipeNeed2);
      patchList('pipes', pid, (x) => ({ points: x.points.filter((_, j) => j !== i) }));
    },
    moveVertex: (pid, i, pt) => patchList('pipes', pid, (pp) => ({ points: pp.points.map((x, j) => (j === i ? pt : x)) })),
    updatePipe: (id, patch) => patchList('pipes', id, patch),
    deletePipe: async (id) => { if (!(await askConfirm(t.confirmDelPipe, { ok: t.pitDelete, cancel: t.btnCancel }))) return false; updateProject((p) => ({ ...p, pipes: p.pipes.filter((x) => x.id !== id) })); return true; },
    updateFitting: (id, patch) => patchList('fittings', id, patch),
    deleteFitting,
    lastFit,
    setLastFit,
    startItem,
    repId,
  };
}
